import { useRef } from 'react'
import { Button } from './Button.tsx'
import { useConfirm } from './Confirm.tsx'
import { icons } from './icons.ts'
import { Tag } from './Tag.tsx'
import { Tooltip } from './Tooltip.tsx'
import type { ManualAttachments } from '../lib/useManualAttachments.ts'

/**
 * Attaching a receita the ERP does not have (#276).
 *
 * For the orçamento whose receita never reached the pharmacy's system, and for
 * the one whose receita did and was not recognised: the file goes in, the
 * analysis starts over, and it is read exactly as an ERP attachment would be.
 *
 * Unavailable while an analysis is running -- the server refuses it then,
 * because the run under way has already listed what it will read.
 */
export function AttachReceitaButton({
  manual,
  disabled,
}: {
  manual: ManualAttachments
  disabled: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={input}
        type="file"
        // What the server can make pages from: images and PDFs.
        accept="image/*,application/pdf"
        className="hidden"
        data-testid="attach-receita-input"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // Cleared, so choosing the same file again still fires.
          event.target.value = ''
          if (file !== undefined) void manual.attach(file)
        }}
      />
      <Button
        variant="secondary"
        tone="brand"
        size="small"
        leadingIcon={icons.attach}
        disabled={disabled || manual.busy}
        title={disabled ? 'Aguarde a análise terminar para anexar' : undefined}
        onClick={() => input.current?.click()}
      >
        {manual.busy ? 'Enviando…' : 'Anexar receita'}
      </Button>
    </>
  )
}

/**
 * The mark beside the Receita title while the document on screen is one a
 * person attached here, with what to know about it and the way to remove it.
 *
 * In the header rather than above the image: a box over the page pushed the
 * receita down and repeated what the title row has room to say. Who attached
 * it, and when, is in the tooltip -- worth finding, not worth the space.
 *
 * Only these can be removed. The ERP's attachments are the pharmacy's record,
 * and are changed in the pharmacy's system, not in ours -- so nothing is
 * offered for them.
 */
export function ManualReceitaMark({
  manual,
  shownAttachmentId,
  disabled,
}: {
  manual: ManualAttachments
  /** The attachment the viewer has on screen. */
  shownAttachmentId: string | undefined
  disabled: boolean
}) {
  const confirm = useConfirm()
  const one = manual.attachments.find((each) => each.id === shownAttachmentId)
  if (one === undefined) return null

  return (
    <div className="flex items-center gap-1" data-manual-receita>
      <Tooltip
        text={`Anexada por ${one.uploadedBy} em ${new Date(one.createdAt).toLocaleString('pt-BR')}`}
        placement="bottom"
      >
        <Tag tone="brand" variant="filled">
          Anexada manualmente
        </Tag>
      </Tooltip>
      <Button
        size="icon"
        variant="tertiary"
        tone="danger"
        aria-label={`Remover ${one.filename}`}
        leadingIcon={icons.remove}
        disabled={disabled || manual.busy}
        onClick={async () => {
          const sure = await confirm({
            title: `Remover ${one.filename}?`,
            message:
              'A receita sai desta conferência e a comparação é refeita sem ela. Os anexos do ERP não mudam.',
            confirmLabel: 'Remover',
            tone: 'danger',
          })
          if (sure) void manual.remove(one.id)
        }}
      />
    </div>
  )
}

/** Why attaching or removing a receita did not work, when it did not. */
export function ManualAttachmentError({ manual }: { manual: ManualAttachments }) {
  if (manual.error === undefined) return null
  return <p className="text-tP text-text-error">{manual.error}</p>
}
